import React from 'react'

import { FLAG_COLOURS } from 'constants/colors'
import { Child } from 'services/child/constants'
import { NurserySettings, NurserySettingsFormBuilder } from 'services/nurseries/models'
import { FormPreviewFormValues, RegistrationForm, RegistrationFormStatus } from 'services/registrationForms/models'
import { Subdomain } from 'services/subdomain/models'

import { ExternalPage, Icon, Page, Space, Spinner, Typography } from 'components'

import i18n from 'translations'

import FormPreview from './components/FormPreview'
import { StyledCircle, StyledWrapper } from './RegistrationFormStyled'

interface RegistrationFormViewProps {
  child: Child
  initialValues: FormPreviewFormValues
  isFinanceV3Enabled: boolean
  isLoading: boolean
  isSubmittedSuccessfully: boolean
  isSubmitting: boolean
  isSubmittingAsCompleted: boolean
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  nurserySettings: NurserySettings
  onSubmit: (values: FormPreviewFormValues, status: RegistrationFormStatus) => void
  registrationForm: RegistrationForm
  subdomain: Subdomain
}

const RegistrationFormView = ({
  child,
  initialValues,
  isFinanceV3Enabled,
  isLoading,
  isSubmittedSuccessfully,
  isSubmitting,
  isSubmittingAsCompleted,
  nurseryFormBuilderSettings,
  nurserySettings,
  onSubmit,
  registrationForm,
  subdomain,
}: RegistrationFormViewProps) => {
  if (isLoading || null === initialValues) {
    return (
      <React.Fragment>
        <Space space="20px" />
        <Spinner />
        <Space space="20px" />
      </React.Fragment>
    )
  }

  const renderContent = () => {
    if (isSubmittedSuccessfully) {
      return (
        <StyledWrapper>
          <StyledCircle $color={FLAG_COLOURS.SUCCESS_LIGHT}>
            <Icon color={FLAG_COLOURS.SUCCESS} height={34} icon="confirmed" />
          </StyledCircle>
          <Typography fontSize={20} fontWeight="600">
            {i18n.t('module:RegistrationForm:afterSent')}
          </Typography>
          <Typography>
            {i18n.t('module:RegistrationForm:afterSentDescription')}
          </Typography>
        </StyledWrapper>
      )
    }

    if (registrationForm.status === RegistrationFormStatus.COMPLETED) {
      return (
        <StyledWrapper>
          <StyledCircle $color={FLAG_COLOURS.ERROR_LIGHT}>
            <Icon color={FLAG_COLOURS.ERROR} height={34} icon="blocked" />
          </StyledCircle>
          <Typography fontSize={20} fontWeight="600">
            {i18n.t('module:RegistrationForm:completed')}
          </Typography>
          <Typography>
            {i18n.t('module:RegistrationForm:completedDescription')}
          </Typography>
        </StyledWrapper>
      )
    }

    if (registrationForm.status === RegistrationFormStatus.LINK_EXPIRED) {
      return (
        <StyledWrapper>
          <StyledCircle $color={FLAG_COLOURS.WARNING_LIGHT}>
            <Icon color={FLAG_COLOURS.WARNING} height={34} icon="link-expired" />
          </StyledCircle>
          <Typography fontSize={20} fontWeight="600">
            {i18n.t('module:RegistrationForm:linkExpired')}
          </Typography>
          <Typography>
            {i18n.t('module:RegistrationForm:linkExpiredDescription')}
            <br />
            {i18n.t('module:RegistrationForm:linkExpiredDescription2')}
          </Typography>
        </StyledWrapper>
      )
    }

    return (
      <Page.Section title={i18n.t('module:RegistrationForm:title')}>
        <FormPreview
          child={child}
          initialValues={initialValues}
          isFinanceV3Enabled={isFinanceV3Enabled}
          isSubmitting={isSubmitting}
          isSubmittingAsCompleted={isSubmittingAsCompleted}
          nurseryFormBuilderSettings={nurseryFormBuilderSettings}
          nurserySettings={nurserySettings}
          registrationForm={registrationForm}
          onSubmit={(values, status) => onSubmit(values, status)}
        />
      </Page.Section>
    )
  }

  return (
    <React.Fragment>
      <ExternalPage.NurseryHeader subdomainData={subdomain} />
      {renderContent()}
    </React.Fragment>
  )
}

export default RegistrationFormView
